import { NormalizedUnitNumber } from '@/domain/types/NumericValues'

import { CAP_WARNING_THRESHOLD, EulerBorrowStatus, EulerBorrowValidationParams, EulerValidationResult } from './types'
import { calculateCapUtilization, calculateRemainingCapacity } from './validateEulerSupply'

/**
 * Validate borrow action for Euler vaults
 */
export function validateEulerBorrow(params: EulerBorrowValidationParams): EulerValidationResult {
  const { value, totalBorrowed, borrowCap, availableLiquidity, isPaused, symbol } = params

  // Check if vault is paused
  if (isPaused) {
    return {
      status: 'paused',
      message: 'Borrowing is currently paused for this vault',
      severity: 'error',
      canProceed: false,
    }
  }

  // Check liquidity first - most critical
  if (availableLiquidity.isZero()) {
    return {
      status: 'no-liquidity',
      message: 'No liquidity available for borrowing',
      severity: 'error',
      canProceed: false,
    }
  }

  // Check if user wants to borrow more than available
  if (value?.gt(availableLiquidity)) {
    return {
      status: 'no-liquidity',
      message: `Insufficient liquidity. Maximum available: ${availableLiquidity.toFixed(2)}${symbol ? ` ${symbol}` : ''}`,
      severity: 'error',
      canProceed: false,
    }
  }

  // Check borrow cap
  if (borrowCap && !borrowCap.isZero()) {
    const utilization = calculateCapUtilization(totalBorrowed, borrowCap)
    const remaining = calculateRemainingCapacity(totalBorrowed, borrowCap)

    // Check if cap is reached
    if (utilization !== undefined && utilization >= 1) {
      return {
        status: 'cap-reached',
        message: 'Borrow cap has been reached. No additional borrowing is allowed.',
        severity: 'error',
        canProceed: false,
      }
    }

    // Check if value would exceed cap
    if (value && remaining && value.gt(remaining)) {
      return {
        status: 'cap-reached',
        message: `Your borrow would exceed the borrow cap. Maximum borrow: ${remaining.toFixed(2)}${symbol ? ` ${symbol}` : ''}`,
        severity: 'error',
        canProceed: false,
      }
    }

    // Check if approaching cap (warning threshold)
    if (utilization !== undefined && utilization >= CAP_WARNING_THRESHOLD) {
      const remainingStr = remaining ? remaining.toFixed(2) : 'N/A'
      return {
        status: 'cap-warning',
        message: `Borrow cap is almost reached (${(utilization * 100).toFixed(1)}% utilized). Remaining capacity: ${remainingStr}`,
        severity: 'warning',
        canProceed: true,
      }
    }
  }

  // Check low liquidity warning
  // Using a simple threshold - if liquidity is very low relative to typical operations
  const liquidityValue = availableLiquidity.toNumber()
  if (liquidityValue > 0 && liquidityValue < 1000) {
    // Less than 1000 units
    return {
      status: 'low-liquidity',
      message: `Low liquidity available: ${availableLiquidity.toFixed(2)}${symbol ? ` ${symbol}` : ''}`,
      severity: 'warning',
      canProceed: true,
    }
  }

  return {
    status: 'available',
    severity: 'info',
    canProceed: true,
  }
}

/**
 * Get borrow status without validating a specific value
 */
export function getEulerBorrowStatus(
  totalBorrowed: NormalizedUnitNumber,
  borrowCap: NormalizedUnitNumber | undefined,
  availableLiquidity: NormalizedUnitNumber,
  isPaused?: boolean,
): EulerBorrowStatus {
  if (isPaused) return 'paused'

  if (availableLiquidity.isZero()) return 'no-liquidity'

  if (borrowCap && !borrowCap.isZero()) {
    const utilization = calculateCapUtilization(totalBorrowed, borrowCap)
    if (utilization !== undefined) {
      if (utilization >= 1) return 'cap-reached'
      if (utilization >= CAP_WARNING_THRESHOLD) return 'cap-warning'
    }
  }

  // Check low liquidity warning
  const liquidityValue = availableLiquidity.toNumber()
  if (liquidityValue > 0 && liquidityValue < 1000) {
    return 'low-liquidity'
  }

  return 'available'
}
